import { useState } from 'react';

const SERVICE_OPTIONS = [
  'Photometric Lighting Design',
  'Commercial LED Upgrade',
  'Tennis Court / Sports Lighting',
  'Car Dealership Lighting',
  'Retail / Shopping Center Lighting',
  'Industrial / High Bay Lighting',
  'Solar All-In-One Lighting',
  'Agricultural Lighting',
  'Multi-Family Lighting',
  'Sign Lighting',
  'New Construction Specification',
  'Other',
];

export default function LeadForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('loading');
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));

    try {
      const res = await fetch('https://readdy.ai/api/form/d72o1e2aegmdqoca8260', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border border-gold/25 p-12 text-center bg-white/4">
        <div className="w-14 h-14 flex items-center justify-center border border-gold/40 mx-auto mb-6">
          <i className="ri-check-line text-gold text-2xl"></i>
        </div>
        <h3 className="font-serif text-white text-2xl mb-3 font-light">
          Message Received
        </h3>
        <p className="text-white/45 text-sm leading-relaxed mb-6">
          Thank you for reaching out. Our team will be in touch shortly to discuss your commercial lighting project.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="text-gold text-[9px] font-bold tracking-[0.28em] uppercase hover:text-gold-light transition-colors cursor-pointer"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white/[0.04] border border-white/8 p-8 lg:p-10 space-y-5"
    >
      <div className="mb-7">
        <h3 className="font-serif text-white text-xl font-light mb-1">
          Request a Free Assessment
        </h3>
        <p className="text-white/35 text-xs tracking-wide">
          Commercial properties only · No obligation
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[9px] font-semibold tracking-[0.28em] uppercase text-white/35 mb-2">
            Full Name <span className="text-gold">*</span>
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder="Your name"
            className="w-full bg-white/4 border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
          />
        </div>
        <div>
          <label className="block text-[9px] font-semibold tracking-[0.28em] uppercase text-white/35 mb-2">
            Email Address <span className="text-gold">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="you@company.com"
            className="w-full bg-white/4 border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
          />
        </div>
      </div>

      <div>
        <label className="block text-[9px] font-semibold tracking-[0.28em] uppercase text-white/35 mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          name="phone"
          placeholder="(727) 000-0000"
          className="w-full bg-white/4 border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
        />
      </div>

      <div>
        <label className="block text-[9px] font-semibold tracking-[0.28em] uppercase text-white/35 mb-2">
          Service Needed
        </label>
        <select
          name="service"
          className="w-full bg-charcoal border border-white/10 text-white/60 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300 cursor-pointer"
        >
          <option value="">Select a service...</option>
          {SERVICE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[9px] font-semibold tracking-[0.28em] uppercase text-white/35 mb-2">
          Project Details
        </label>
        <textarea
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Describe your property and lighting goals..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full bg-white/4 border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300 resize-none"
        />
        {charCount > 450 && (
          <p className={`text-right text-xs mt-1 ${charCount >= 500 ? 'text-red-400' : 'text-white/30'}`}>
            {charCount}/500
          </p>
        )}
      </div>

      {status === 'error' && (
        <p className="text-red-400/80 text-xs tracking-wide">
          Something went wrong. Please try again or call 727-914-9002.
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'loading' || charCount > 500}
        className="w-full whitespace-nowrap bg-gold text-charcoal text-[10px] font-bold tracking-[0.25em] uppercase py-4 hover:bg-gold-light transition-colors duration-300 disabled:opacity-50 cursor-pointer mt-2"
      >
        {status === 'loading' ? 'Sending...' : 'Submit Request'}
      </button>
    </form>
  );
}
